import api from "../utils/api";

// Types matching backend DTOs

// Card types
export interface NewApplications {
  period: string; // e.g., "24h"
  count: number;
  previousPeriodCount: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface PendingApplications {
  count: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface UnreadApplications {
  count: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface NewActivity {
  period: string; // e.g., "24h"
  count: number;
  previousPeriodCount: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface RecentBookings {
  period: string; // e.g., "24h"
  count: number;
  previousPeriodCount: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface CoreFailures {
  count: number;
  minCreatedAt?: string; // ISO 8601
  maxCreatedAt?: string; // ISO 8601
}

export interface OverviewMetrics {
  volume: {
    byStatus: Array<{
      title: string;
      count: number;
      sortOrder: number;
    }>;
    byFlow: Array<{
      id: string;
      flowSlug: string;
      flowTitle: string;
      count: number;
    }>;
    unassigned: number;
    unassignedThreshold: number;
  };
  processing: {
    avgTimeToDecision: number; // Minutes
    avgTimeToDecisionChange: number; // Minutes
    avgTimeSinceLastActivity: number; // Minutes
    efficiency: {
      percentage: number;
      percentageChange: number;
      distribution: {
        lessThan1d: number;
        oneTo2d: number;
        twoTo4d: number;
        fourTo7d: number;
        moreThan7d: number;
      };
    };
    aging: {
      percentage: number;
      distribution: {
        lessThan1d: number;
        oneTo3d: number;
        threeTo7d: number;
        sevenTo14d: number;
        moreThan14d: number;
      };
    };
  };
  workload: {
    byAdmin: Array<{
      adminId: number;
      adminName: string;
      assignedCount: number;
    }>;
  };
  systemHealth: {
    autoDecisionCompletionRate: number;
    autoDecisionCompletionRateChange: number;
  };
  metadata: {
    timestamp: string;
    cacheUntil?: string;
  };
}

export interface AlertItem {
  id: string;
  severity: "critical" | "warning" | "info";
  type: string;
  title: string;
  description: string;
  assignedToMeCount: number;
  unassignedCount: number;
  breakdown?: {
    // For stalled applications
    infoDays: { count: number; label: string }; // "3-7d"
    warningDays: { count: number; label: string }; // "1-2w"
    criticalDays: { count: number; label: string }; // "2w+"
  };
  mostRecentTimestamp?: string; // ISO 8601
  duration?: string; // e.g., "24h"
  drilldownFilter?: {
    assignedToMe?: boolean;
    decisionStatus?: string;
    customFilter?: string;
    sortBy?: string;
  };
  drilldownTitle?: string; // Title for assigned drilldown
  unassignedDrilldownFilter?: {
    assignedToMe?: boolean;
    decisionStatus?: string;
    customFilter?: string;
    sortBy?: string;
  };
  unassignedDrilldownTitle?: string; // Title for unassigned drilldown
}

export interface OverviewAlerts {
  alerts: AlertItem[];
  criticalMetrics: {
    unassigned: number;
    unassignedThreshold: number;
    failedBookings: number;
    lastFailureTime?: string;
  };
  metadata: {
    timestamp: string;
    nextRefreshAt?: string;
  };
}

export interface OverviewTrends {
  volume: {
    byDay: Array<{
      date: string;
      received: number;
      approved: number;
      denied: number;
    }>;
  };
  processingTime: {
    byWeek: Array<{
      weekStart: string;
      avgHours: number;
      count: number;
    }>;
  };
  statusDistribution: {
    byDay: Array<{
      date: string;
      statuses: Record<string, number>;
    }>;
  };
  metadata: {
    timestamp: string;
    cacheUntil?: string;
  };
}

// New aggregate types from backend refactoring
export interface AggregateByDecisionTime {
  average: number; // Minutes - average time to decision
  previousAverage: number; // Minutes - average from previous period
  distribution: {
    lessThan1d: number;
    oneTo2d: number;
    twoTo4d: number;
    fourTo7d: number;
    moreThan7d: number;
  };
  previousDistribution: {
    lessThan1d: number;
    oneTo2d: number;
    twoTo4d: number;
    fourTo7d: number;
    moreThan7d: number;
  };
}

export interface AggregateByLastActivity {
  average: number; // Minutes - average time since last activity
  distribution: {
    lessThan1d: number;
    oneTo3d: number;
    threeTo7d: number;
    sevenTo14d: number;
    moreThan14d: number;
  };
}

export type AggregateByAdmin = Array<{
  id: number;
  firstName: string;
  lastName: string;
  count: number;
}>;

export type AggregateBySubmissionDate = Array<{
  date: string; // YYYY-MM-DD
  received: number;
  approved: number;
  denied: number;
  canceled: number;
}>;

export type AggregateByDecisionStatus = Array<{
  id: string;
  title: string;
  count: number;
}>;

export type AggregateByFlow = Array<{
  id: string;
  title: string;
  received: number;
  approved: number;
  denied: number;
  canceled: number;
}>;

export interface DecisionMetrics {
  period: string; // e.g., "30d"
  count: number; // Total applications in period
  previousPeriodCount: number; // Total applications in previous period
  autoDecisions: number; // Auto-decisioned applications in period
  previousPeriodAutoDecisions: number; // Auto-decisioned applications in previous period
  approved: number; // Approved applications in period
  previousPeriodApproved: number; // Approved applications in previous period
  denied: number; // Denied applications in period
  previousPeriodDenied: number; // Denied applications in previous period
  canceled: number; // Canceled applications in period
  previousPeriodCanceled: number; // Canceled applications in previous period
}

// Consolidated applications-overview data type
export interface OverviewData {
  // Card data (base level)
  newApplications: NewApplications;
  pendingApplications: PendingApplications;
  unreadApplications: UnreadApplications;
  newActivity: NewActivity;
  recentBookings: RecentBookings;
  coreFailures?: CoreFailures;

  // Legacy data structures (for backwards compatibility)
  volume?: OverviewMetrics["volume"]; // Optional for backwards compatibility
  processing?: OverviewMetrics["processing"]; // Optional for backwards compatibility
  workload?: OverviewMetrics["workload"]; // Optional for backwards compatibility
  trends?: {
    volumeByDay: OverviewTrends["volume"]["byDay"];
  };

  // New aggregate structures
  aggregateByDecisionStatus?: AggregateByDecisionStatus;
  aggregateByFlow?: AggregateByFlow;
  unassignedCount?: number;
  aggregateByDecisionTime?: AggregateByDecisionTime;
  aggregateByLastActivity?: AggregateByLastActivity;
  aggregateByAdmin?: AggregateByAdmin;
  aggregateBySubmissionDate?: AggregateBySubmissionDate;

  decisionMetrics?: DecisionMetrics;
  systemHealth?: OverviewMetrics["systemHealth"]; // Legacy - for backwards compatibility
  metadata: {
    timestamp: string;
    cacheUntil?: string;
  };
}

/**
 * Fetch frequently updating overview metrics for an organization
 * Includes: cards, workload, status queues, recent volume, last activity
 * Refresh: Every 30 seconds to 2 minutes
 */
export const fetchOverviewMetrics = async (
  organizationKey: string,
  includeArchived: boolean = false
): Promise<Partial<OverviewData>> => {
  const params = new URLSearchParams();
  if (includeArchived) {
    params.append("includeArchived", "true");
  }

  const response = await api.get(
    `/organizations/${organizationKey}/submissions/dashboard/metrics${
      params.toString() ? `?${params.toString()}` : ""
    }`
  );

  return response.data;
};

/**
 * Fetch overview alerts for an organization
 */
export const fetchOverviewAlerts = async (
  organizationKey: string,
  severity?: "critical" | "warning" | "info",
  includeArchived: boolean = false
): Promise<OverviewAlerts> => {
  const params = new URLSearchParams();
  if (severity) {
    params.append("severity", severity);
  }
  if (includeArchived) {
    params.append("includeArchived", "true");
  }

  const response = await api.get(
    `/organizations/${organizationKey}/submissions/dashboard/alerts${
      params.toString() ? `?${params.toString()}` : ""
    }`
  );

  return response.data;
};

/**
 * Fetch slowly changing overview trend data for an organization
 * Includes: outcomes, efficiency, flow distribution (30-day data)
 * Refresh: Every 12-24 hours
 */
export const fetchOverviewTrends = async (
  organizationKey: string,
  includeArchived: boolean = false
): Promise<Partial<OverviewData>> => {
  const params = new URLSearchParams();
  if (includeArchived) {
    params.append("includeArchived", "true");
  }

  const response = await api.get(
    `/organizations/${organizationKey}/submissions/dashboard/trends${
      params.toString() ? `?${params.toString()}` : ""
    }`
  );

  return response.data;
};
